import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Spin, Empty, DatePicker, Select, Button, Tooltip, Icon } from 'antd';
import { Sankey, ResponsiveContainer, Layer, Rectangle } from 'recharts';
import moment from 'moment';
import { getHiveMovementFlow } from '../../../../services/apiaryService';
import './NetworkFlow.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const NetworkFlow = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ nodes: [], links: [], metadata: {} });
    const [filters, setFilters] = useState({
        dateRange: [moment().subtract(6, 'months'), moment()],
        batchId: null
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                startDate: filters.dateRange?.[0]?.format('YYYY-MM-DD'),
                endDate: filters.dateRange?.[1]?.format('YYYY-MM-DD'),
                batch_id: filters.batchId
            };
            const result = await getHiveMovementFlow(params);
            
            if (result && result.nodes && result.links) {
                // Initialize degrees
                const nodes = result.nodes.map(n => ({ ...n, inDegree: 0, outDegree: 0 }));
                
                const links = result.links.map(l => {
                    const sourceIdx = nodes.findIndex(n => n.id === l.source);
                    const targetIdx = nodes.findIndex(n => n.id === l.target);
                    
                    if (sourceIdx !== -1) nodes[sourceIdx].outDegree += 1;
                    if (targetIdx !== -1) nodes[targetIdx].inDegree += 1;

                    return {
                        source: sourceIdx,
                        target: targetIdx,
                        value: l.value,
                        original: l
                    };
                }).filter(l => l.source !== -1 && l.target !== -1);

                setData({ ...result, nodes, links });
            }
        } catch (error) {
            console.error('Failed to fetch movement flow:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [filters]);

    const MyNode = (props) => {
        const { x, y, width, height, index } = props;
        const node = data.nodes[index];
        
        // Premium Card Dimensions
        const cardWidth = 220;
        const cardHeight = 84; 
        
        const isSource = node.inDegree === 0;
        const isTarget = node.outDegree === 0;
        
        let labelX;
        if (isSource) {
            // Source nodes: Put label to the RIGHT of the bar (flows under the card)
            labelX = x + width + 16;
        } else if (isTarget) {
            // Target nodes: Put label to the LEFT of the bar (flows into the card)
            labelX = x - cardWidth - 16;
        } else {
            // Intermediate nodes. Put label centered above.
            labelX = x - (cardWidth / 2) + (width / 2);
        }

        // Ensure label stays within absolute SVG bounds just in case
        labelX = Math.max(0, Math.min(labelX, 2000));


        // Vertical centering
        const labelY = y + (height / 2) - (cardHeight / 2);

        return (
            <Layer key={`node-${index}`}>
                {/* Node Connection Bar */}
                <Rectangle
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill="url(#nodeGradient)"
                    fillOpacity={1}
                    rx={4}
                    ry={4}
                    className="sankey-bar"
                />
                
                {/* Premium HTML Card */}
                <foreignObject 
                    x={labelX} 
                    y={labelY} 
                    width={cardWidth} 
                    height={cardHeight + 10} 
                    style={{ overflow: 'visible' }}
                >
                    <div 
                        className={`sankey-node-card ${node.isActive ? 'active' : 'inactive'}`}
                        onClick={() => {
                            if (node.link) window.open(node.link, '_blank');
                        }}
                    >
                        <div className="flex items-start justify-between mb-2">
                            <div className="node-title truncate flex-1" title={node.name}>
                                {node.name}
                            </div>
                            <span className={`type-badge ${node.type.toLowerCase()}`}>
                                {node.type}
                            </span>
                        </div>
                        
                        <div className="node-location truncate text-xs text-gray-500 flex items-center" title={node.location}>
                            <Icon type="environment" className="mr-1 opacity-70" />
                            {node.location}
                        </div>
                        
                        <div className="node-status mt-2 flex items-center text-xs font-semibold">
                            <span className={`status-dot ${node.status.toLowerCase()}`}></span>
                            {node.status}
                        </div>
                    </div>
                </foreignObject>
            </Layer>
        );
    };

    // Calculate dynamic height based on node count to prevent overlap
    const calculateDynamicHeight = () => {
        if (!data.nodes || data.nodes.length === 0) return 500;
        // Assume maximum density is on the right side
        const targetNodesCount = data.nodes.filter(n => n.outDegree === 0).length || Math.ceil(data.nodes.length / 2);
        const estimatedHeight = targetNodesCount * 110; 
        return Math.max(600, estimatedHeight);
    };

    const chartHeight = calculateDynamicHeight();

    return (
        <div className="network-flow-container p-6">
            <div className="page-header mb-6">
                <Row justify="space-between" align="middle">
                    <Col>
                        <h2 className="text-3xl font-extrabold text-gray-800 m-0 tracking-tight flex items-center">
                            <div className="icon-wrapper mr-3">
                                <Icon type="deployment-unit" className="text-white text-xl" />
                            </div>
                            Hive Network Flow
                        </h2>
                        <p className="text-gray-500 mt-1 text-sm font-medium">Visualize and analyze hive traffic across your entire organization</p>
                    </Col>
                    <Col>
                        <div className="filter-actions flex items-center space-x-4 bg-white p-2 rounded-xl shadow-sm border border-gray-100">
                            <RangePicker 
                                value={filters.dateRange}
                                onChange={(dates) => setFilters(f => ({ ...f, dateRange: dates }))}
                                className="premium-datepicker border-0 shadow-none"
                                separator="→"
                            />
                            <div className="h-6 w-px bg-gray-200"></div>
                            <Button 
                                type="primary" 
                                icon="reload" 
                                onClick={fetchData}
                                className="premium-refresh-btn h-8 rounded-lg shadow-md font-semibold px-4"
                                loading={loading}
                            >
                                Refresh Data
                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Summary Stats */}
            <Row gutter={24} className="mb-6">
                <Col span={8}>
                    <Card className="stat-card glassmorphism hover-lift" bordered={false}>
                        <Statistic
                            title={<span className="text-gray-500 font-semibold tracking-wide uppercase text-xs">Total Hives Moved</span>}
                            value={data.metadata?.total_volume || 0}
                            prefix={<Icon type="swap" className="mr-2 text-orange-500" />}
                            valueStyle={{ color: '#1f2937', fontWeight: '800', fontSize: '28px' }}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card className="stat-card glassmorphism hover-lift" bordered={false}>
                        <Statistic
                            title={<span className="text-gray-500 font-semibold tracking-wide uppercase text-xs">Active Locations</span>}
                            value={data.nodes?.length || 0}
                            prefix={<Icon type="environment" className="mr-2 text-green-500" />}
                            valueStyle={{ color: '#1f2937', fontWeight: '800', fontSize: '28px' }}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card className="stat-card glassmorphism hover-lift" bordered={false}>
                        <Statistic
                            title={<span className="text-gray-500 font-semibold tracking-wide uppercase text-xs">Unique Routes</span>}
                            value={data.links?.length || 0}
                            prefix={<Icon type="branches" className="mr-2 text-blue-500" />}
                            valueStyle={{ color: '#1f2937', fontWeight: '800', fontSize: '28px' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Main Visualizer */}
            <Card className="viz-card premium-shadow bg-white rounded-2xl overflow-hidden" bordered={false}>
                <div className="border-b border-gray-100 pb-4 mb-4 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-gray-800 m-0">Movement Topology</h3>
                    <span className="text-xs text-gray-400 font-medium bg-gray-50 px-3 py-1 rounded-full border border-gray-100">
                        Interactive Map
                    </span>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center" style={{ height: '500px' }}>
                        <Spin size="large" tip="Analyzing routing topology..." />
                    </div>
                ) : data.links.length > 0 ? (
                    <div className="scrollable-viz-container">
                        <div className="sankey-wrapper relative" style={{ height: `${Math.max(600, chartHeight)}px`, minWidth: '1100px', width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <Sankey
                                data={data}
                                node={<MyNode />}
                                nodePadding={80}
                                nodeWidth={12}
                                margin={{ left: 40, right: 40, top: 60, bottom: 60 }}
                                link={{ stroke: 'url(#linkGradient)', strokeOpacity: 0.35 }}
                                iterations={64}
                            >
                                <defs>
                                    <linearGradient id="linkGradient" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0%" stopColor="#ffd591" />
                                        <stop offset="100%" stopColor="#ff9c6e" />
                                    </linearGradient>
                                    <linearGradient id="nodeGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#fa8c16" />
                                        <stop offset="100%" stopColor="#d46b08" />
                                    </linearGradient>
                                </defs>
                                <Tooltip 
                                    cursor={{ fill: 'rgba(250, 140, 22, 0.1)' }}
                                    content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                            const item = payload[0];
                                            if (item.payload.name) {
                                                // Node Tooltip
                                                return (
                                                    <div className="premium-tooltip node-tooltip">
                                                        <div className="tooltip-header">
                                                            <div className="font-bold text-gray-800">{item.payload.name}</div>
                                                            <div className="text-xs text-gray-500">{item.payload.location}</div>
                                                        </div>
                                                        <div className="tooltip-body mt-2">
                                                            <div className="flex justify-between text-sm">
                                                                <span className="text-gray-500">Total In/Out Volume:</span>
                                                                <span className="font-bold text-orange-600">{item.value} Hives</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            } else {
                                                // Link Tooltip
                                                const linkData = item.payload.original;
                                                return (
                                                    <div className="premium-tooltip link-tooltip">
                                                        <div className="flex items-center space-x-2 mb-3">
                                                            <span className="font-semibold text-gray-700 max-w-xs truncate">{data.nodes[item.payload.source].name}</span>
                                                            <Icon type="arrow-right" className="text-orange-400 text-xs" />
                                                            <span className="font-semibold text-gray-700 max-w-xs truncate">{data.nodes[item.payload.target].name}</span>
                                                        </div>
                                                        <div className="flex justify-between items-center bg-orange-50 p-2 rounded-lg border border-orange-100 mb-3">
                                                            <span className="text-xs font-bold text-orange-800 uppercase">Transfer Volume</span>
                                                            <span className="text-lg font-black text-orange-600">{item.value} Hives</span>
                                                        </div>
                                                        {linkData?.purposes && (
                                                            <div className="purposes-breakdown">
                                                                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Purpose Breakdown</p>
                                                                <div className="space-y-1">
                                                                    {Object.entries(linkData.purposes).map(([p, v]) => (
                                                                        <div key={p} className="flex justify-between text-xs items-center">
                                                                            <span className="text-gray-600 font-medium capitalize">{p.replace(/_/g, ' ').toLowerCase()}</span>
                                                                            <span className="font-bold text-gray-800 bg-gray-100 px-2 py-0.5 rounded-md">{v}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            }
                                        }
                                        return null;
                                    }}
                                />
                            </Sankey>
                        </ResponsiveContainer>
                    </div>
                    </div>
                ) : (
                    <Empty 
                        image={Empty.PRESENTED_IMAGE_SIMPLE} 
                        description={<span className="text-gray-500 font-medium">No movement topology found for this period.</span>} 
                        className="py-16"
                    />
                )}
            </Card>

            <div className="info-footer mt-6 text-center">
                <p className="text-gray-400 text-xs font-medium">
                    <Icon type="info-circle" className="mr-1" />
                    Path width correlates with hive transfer volume. Nodes auto-arrange to optimize flow visualization.
                </p>
            </div>
        </div>
    );
};

export default NetworkFlow;
;
